export const SUPPORTED_LOCALES = ['fr', 'en'] as const;

export type Locale = (typeof SUPPORTED_LOCALES)[number];
export type TranslationCatalog = Record<string, string>;

const STORAGE_KEY = 'mediland.locale';

export function isLocale(value: unknown): value is Locale {
    return typeof value === 'string' && SUPPORTED_LOCALES.includes(value.toLowerCase() as Locale);
}

export function normalizeLocale(value: unknown): Locale | undefined {
    if (typeof value !== 'string') return undefined;
    const normalized = value.trim().toLowerCase().split('-')[0];
    return isLocale(normalized) ? normalized : undefined;
}

export function resolveLocale(preferred?: unknown): Locale {
    const queryLocale = normalizeLocale(new URLSearchParams(window.location.search).get('lang'));
    if (queryLocale) return queryLocale;

    const explicitLocale = normalizeLocale(preferred);
    if (explicitLocale) return explicitLocale;

    try {
        const storedLocale = normalizeLocale(window.localStorage.getItem(STORAGE_KEY));
        if (storedLocale) return storedLocale;
    } catch {
        // localStorage may be disabled; browser language and French fallback remain available.
    }

    return normalizeLocale(window.navigator.language) ?? 'fr';
}

export function persistLocale(locale: Locale, updateUrl = true): void {
    try {
        window.localStorage.setItem(STORAGE_KEY, locale);
    } catch {
        // A private browsing policy can disable localStorage.
    }
    document.documentElement.lang = locale;

    if (updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('lang', locale);
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }
}

export function interpolate(message: string, params: Record<string, string | number> = {}): string {
    return message.replace(/\{(\w+)\}/g, (match, key) =>
        Object.prototype.hasOwnProperty.call(params, key) ? String(params[key]) : match,
    );
}

export function createSourceTranslator(locale: Locale, english: TranslationCatalog) {
    return (source: string, params?: Record<string, string | number>): string => {
        const translated = locale === 'en' ? english[source] ?? source : source;
        return interpolate(translated, params);
    };
}

export function createLanguageSwitcher(
    locale: Locale,
    onChange: (locale: Locale) => void | Promise<void>,
    label: string,
): HTMLElement {
    const container = document.createElement('div');
    container.className = 'language-switcher';
    container.setAttribute('role', 'group');
    container.setAttribute('aria-label', label);

    for (const candidate of SUPPORTED_LOCALES) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `language-switcher__button${candidate === locale ? ' is-active' : ''}`;
        button.textContent = candidate.toUpperCase();
        button.lang = candidate;
        button.setAttribute('aria-pressed', candidate === locale ? 'true' : 'false');
        button.addEventListener('click', async () => {
            if (candidate === locale) return;
            persistLocale(candidate);
            await onChange(candidate);
        });
        container.appendChild(button);
    }

    return container;
}

export function installDomTranslator(translator: (source: string) => string): () => void {
    const translateTree = (root: Node) => {
        if (root.nodeType === Node.TEXT_NODE && root.textContent) {
            const translated = translator(root.textContent);
            if (translated !== root.textContent) root.textContent = translated;
            return;
        }
        for (const child of Array.from(root.childNodes)) translateTree(child);
    };

    translateTree(document.body);
    const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            for (const node of Array.from(mutation.addedNodes)) translateTree(node);
        }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
}
